import React, {
	useState, useEffect, useRef,
} from 'react';
import { useParams, useOutletContext } from 'react-router-dom';

import { fetchData } from '../../../helpers/methods';

import CommentInput from '../comments/sub-components/comment-input';
import Comment from './sub-components/comment';

import SpinnerContainer from '../../../component-library/spinner-container';
import FormSelect from '../../../component-library/form-select';

const ContactProvider = () => {
	const { uiMenuConfig } = useOutletContext();
	const { id } = useParams();
	const [loading, setLoading] = useState(true);
	const [conversationId, setConversationId] = useState('');
	const [type, setType] = useState('question');
	const [allCommentsData, setAllCommentsData] = useState([]);
	const [replyNotificationData, setReplyNotificationData] = useState([]);
	const [hasNewReplyData, setHasNewReplyData] = useState([]);
	const allCommentsDataRef = useRef();
	allCommentsDataRef.current = allCommentsData;
	const [newComment, setNewComment] = useState('');
	const [newCommentError, setNewCommentError] = useState('');
	const [totalResults, setTotalResults] = useState(0);
	const totalResultsRef = useRef();
	totalResultsRef.current = totalResults;
	const [pageOffset, setPageOffset] = useState(0);
	const pageOffsetRef = useRef();
	pageOffsetRef.current = pageOffset;
	const pageSize = 20;
	const maxCommentLength = 512;
	const typeFieldOptions = [
		{ id: 'question', text: 'Question', value: 'question' },
		{ id: 'request', text: 'Request', value: 'request' },
	];

	const getAllCommentsData = () => {
		fetchData({
			uri: `/api/web/asks/catitem/${id}?pageSize=${pageSize}`,
			method: 'GET',
		})
			.then(res => {
				const newAllCommentsData = res.body.conversations[0].comments;
				const newConversationId = res.body.conversations[0].conversation.key;
				const newTotalResults = res.body.conversations[0].conversation.last;
				setTotalResults(newTotalResults);
				setPageOffset(0);
				setConversationId(newConversationId);
				setAllCommentsData(newAllCommentsData);
				setLoading(false);
			})
			.catch(() => {
				setLoading(false);
			});
	};

	const getAllCommentsDataPaginated = newOffset => {
		fetchData({
			uri: `/api/web/asks/catitem/${id}?pageSize=${pageSize}&offset=${newOffset}`,
			method: 'GET',
		})
			.then(res => {
				const newComments = res.body.conversations[0].comments;
				const newTotalResults = res.body.conversations[0].conversation.last;
				let newAllCommentsData = allCommentsDataRef.current;
				newAllCommentsData = [...newAllCommentsData, ...newComments];
				setTotalResults(newTotalResults);
				setAllCommentsData(newAllCommentsData);
				setLoading(false);
			})
			.catch(() => {
				setLoading(false);
			});
	};

	const getReplyNotificationData = () => {
		fetchData({
			uri: '/api/web/my/store',
			method: 'GET',
		})
			.then(res => {
				const newRepliesData = res.body.replies;
				const newRepliesArray = [];
				let newHasRepliesArray = [];
				newRepliesData.forEach(reply => {
					const lastBranch = reply.conversationBranches.slice(-1);
					newRepliesArray.push(lastBranch[0].key);
					const hasReplies = reply.conversationBranches.slice(0, -1);
					newHasRepliesArray = [
						...newHasRepliesArray,
						...hasReplies,
					];
				});
				const newUniqueHasRepliesArray = newHasRepliesArray.filter(
					(v, i, a) => a.findIndex(v2 => (JSON.stringify(v2) === JSON.stringify(v))) === i,
				);
				setReplyNotificationData(newRepliesArray);
				setHasNewReplyData(newUniqueHasRepliesArray);
				getAllCommentsData();
			});
	};

	const handleAddComment = () => {
		const commentData = {
			message: newComment,
			styling: type,
		};
		fetchData({
			uri: `/api/web/asks/catitem/${id}/conv/${conversationId}`,
			method: 'POST',
			options: {
				body: JSON.stringify(commentData),
			},
		})
			.then(({ ok }) => {
				if (ok) {
					setLoading(true);
					setNewComment('');
					setNewCommentError('');
					getAllCommentsData();
				} else {
					setNewCommentError('Sorry, your comment could not be posted at this time, please try again.');
				}
			})
			.catch(() => {
				setNewCommentError('Sorry, your comment could not be posted at this time, please try again.');
			});
	};

	const handleAddCommentValidation = () => {
		setNewCommentError('');
		if (newComment !== '') {
			if (newComment.length > maxCommentLength) {
				setNewCommentError(`Comments must be less than ${maxCommentLength} characters in length.`);
			} else {
				handleAddComment();
			}
		} else {
			setNewCommentError('You must enter a comment.');
		}
	};

	const updateTypeField = e => {
		const newTypeField = e.target.value;
		setType(newTypeField);
	};

	const handleScroll = () => {
		const container = document.getElementById('mainContainer');
		if (window.innerHeight + window.pageYOffset >= container.offsetHeight) {
			const newPageOffset = pageOffsetRef.current + pageSize;
			if (newPageOffset <= totalResultsRef.current) {
				setPageOffset(newPageOffset);
				getAllCommentsDataPaginated(newPageOffset);
			}
		}
	};

	useEffect(() => {
		getReplyNotificationData();
		window.addEventListener('scroll', handleScroll);

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	return (
		<>
			<div className="flex-row justify-space-between align-items-flex-start border-bottom margin-bottom-16">
				<h3>{uiMenuConfig?.contactProvider?.label || 'Contact Provider'}</h3>
			</div>
			{loading && (
				<SpinnerContainer padding={false} />
			)}
			{!loading && (
				<div className="comments margin-bottom-24">
					<div className="flex-row">
						<div className="flex-row justify-space-between align-items-flex-start">
							<div className="comment__type-label margin-right-8">I have a:</div>
							<div className="margin-right-16">
								<FormSelect
									action={e => { updateTypeField(e); }}
									data={typeFieldOptions}
									id="sort_field"
									name="sort_field"
									value={type}
								/>
							</div>
						</div>
						<div className="flex-1">
							<CommentInput
								placeholder="Add a comment..."
								formAction={value => setNewComment(value)}
								formValue={newComment}
								formError={newCommentError}
								buttonTitle="Post Comment"
								buttonAction={() => handleAddCommentValidation()}
								maxCommentLength={maxCommentLength}
							/>
						</div>
					</div>
					{allCommentsData?.length ? (
						allCommentsData.map((comment, index) => (
							<Comment
								// eslint-disable-next-line react/no-array-index-key
								key={index}
								id={id}
								comment={comment}
								canReply
								maxCommentLength={maxCommentLength}
								replyNotifications={replyNotificationData}
								hasNewReplyData={hasNewReplyData}
							/>
						))
					) : (
						<p className="grey-text">No comments found</p>
					)}
				</div>
			)}
		</>
	);
};

export default ContactProvider;
